import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Send, ArrowBack } from "@mui/icons-material";
import { useAddMessageMutation } from "../../services/message.query";
import { Notify } from "../../layouts/notify";
import { useNavigate } from "react-router";
import { DefaultPage } from "../../layouts/default-page";

export default function NewMessage() {
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const navigate = useNavigate();
  const [addMessage, { isLoading: messageLoading }] = useAddMessageMutation();

  const handleMessage = (value: string) => {
    setMessage(value);
    if (messageError) setMessageError("");
  };

  const handleOnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      setMessageError("Message is required.");
      return;
    }

    try {
      await addMessage({ message: message.trim() }).unwrap();
      setMessage("");
      setMessageError("");
      Notify("success", "Message broadcast sent successfully");
      navigate("/messages/message-history");
    } catch (error: any) {
      console.error("Error adding message:", error);
      Notify("error", "Failed to send message: " + (error?.data?.message || error?.message || "Unknown error"));
    }
  };

  return (
    <DefaultPage
      title="New Message Broadcast"
      subTitle="Compose and broadcast system announcements to mobile users."
      primaryButtonProps={{
        startIcon: <ArrowBack />,
        children: "View Message History",
        onClick: () => navigate("/messages/message-history"),
      }}
    >
      <Paper
        sx={{
          p: 3.5,
          maxWidth: 800,
          borderRadius: "6px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
          backgroundColor: "#FFFFFF",
        }}
      >
        <form onSubmit={handleOnSubmit}>
          <Stack spacing={3}>
            <Box>
              <Typography fontWeight={700} fontSize={16} color="#0F172A" mb={0.5}>
                Broadcast Content
              </Typography>
              <Typography fontSize={13} color="#64748B">
                Enter the announcement message to be delivered to registered application users.
              </Typography>
            </Box>

            <TextField
              multiline
              rows={6}
              variant="outlined"
              fullWidth
              value={message}
              onChange={(e) => handleMessage(e.target.value)}
              placeholder="Type your announcement or broadcast message here..."
              error={Boolean(messageError)}
              helperText={messageError}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  fontSize: "14px",
                },
              }}
            />

            <Box display="flex" justifyContent="flex-end" gap={1.5} pt={1}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate("/messages/message-history")}
                disabled={messageLoading}
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  px: 2.5,
                  color: "#64748B",
                  borderColor: "#CBD5E1",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={messageLoading}
                startIcon={<Send />}
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  px: 3,
                  backgroundColor: "#0072BC",
                  "&:hover": { backgroundColor: "#005a96" },
                }}
              >
                Send Broadcast
              </LoadingButton>
            </Box>
          </Stack>
        </form>
      </Paper>
    </DefaultPage>
  );
}
